import React, { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { reportsApi } from '../services/api';
import { DailyProfit } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { formatVietnamCurrency } from '../utils/numberFormatter';

const DailyProfitChart: React.FC = () => {
  const { t } = useLanguage();
  const [data, setData] = useState<DailyProfit[]>([]);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState<number>(30);

  const fetchDailyProfitData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await reportsApi.getDailyProfitData(days);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching daily profit data:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchDailyProfitData();
  }, [fetchDailyProfitData]);

  // Format chart data - exclude last day if it has no data, keep all other days
  const chartData = data
    .filter((d, index) => {
      // Check if this is the last day
      const isLastDay = index === data.length - 1;
      // Check if day has data
      const hasData = d.revenue > 0 || d.totalCosts > 0 || d.profit !== 0;
      
      // If it's the last day and has no data, exclude it
      if (isLastDay && !hasData) {
        return false;
      }
      
      // Include all other days
      return true;
    })
    .map(d => ({
      date: new Date(d.date).toLocaleDateString('vi-VN', { month: 'short', day: 'numeric' }),
      [t('profit')]: d.profit,
      [t('revenue')]: d.revenue,
      [t('total.costs')]: d.totalCosts
    }));

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip" style={{ 
          backgroundColor: '#fff', 
          padding: '10px', 
          border: '1px solid #ccc',
          borderRadius: '4px'
        }}>
          <p className="label" style={{ fontWeight: 'bold', marginBottom: '5px' }}>{payload[0].payload.date}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color, margin: '2px 0' }}>
              {entry.name}: {formatVietnamCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="card">
      <div className="card-header">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">{t('daily.profit.chart')}</h5>
          <div>
            <label className="me-2">{t('time.period')}:</label>
            <select 
              className="form-select form-select-sm d-inline-block" 
              style={{ width: 'auto' }}
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            >
              <option value={30}>{t('last.30.days')}</option>
              <option value={60}>{t('last.60.days')}</option>
              <option value={90}>{t('last.90.days')}</option>
            </select>
          </div>
        </div>
      </div>
      <div className="card-body">
        {loading ? (
          <div className="d-flex justify-content-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">{t('loading')}</span>
            </div>
          </div>
        ) : chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis 
                tickFormatter={(value) => {
                  if (value >= 1000000) {
                    return `${(value / 1000000).toFixed(1)}M`;
                  } else if (value >= 1000) {
                    return `${(value / 1000).toFixed(0)}K`;
                  }
                  return value.toString();
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line 
                type="monotone" 
                dataKey={t('profit')} 
                stroke="#28a745" 
                strokeWidth={2}
                dot={false}
              />
              <Line 
                type="monotone" 
                dataKey={t('revenue')} 
                stroke="#007bff" 
                strokeWidth={2}
                dot={false}
              />
              <Line 
                type="monotone" 
                dataKey={t('total.costs')} 
                stroke="#dc3545" 
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-muted text-center">{t('no.data.available')}</p>
        )}
      </div>
    </div>
  );
};

export default DailyProfitChart;
